import React, { useState, useEffect } from 'react'
import { FileText, Download, CheckCircle, XCircle, Eye, Search, Loader } from 'lucide-react'
import dayjs from "dayjs";
import skyautologo from "../../assets/skyAutologo.png"
import { generateGatePassPdf } from "../../utils/gatePassPdf";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { getSellerGatePasses } from "../../services/gatePassService.ts";
import Pagination from "../common/Pagination";
import expirePng from "../../assets/expire.png"
import approvedpng from "../../assets/approved.png"

const HistoryAndGatepass = () => {
  const { translate } = useLanguage();
  const { user } = useAuth();
  const [gatePasses, setGatePasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filterStatus, setFilterStatus] = useState('all') // all, issued, pending, expired
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterStatus]);

  // Fetch gate passes from API
  useEffect(() => {
    const fetchGatePasses = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!user?.seller?.id) {
          setError("Seller identity not found");
          setLoading(false);
          return;
        }

        const params = {
          page: currentPage,
          limit: pageSize,
          sortBy: "id",
          sortOrder: "desc",
          filters: {},
        };

        if (filterStatus !== "all") {
          params.filters.validityStatus = filterStatus;
        }

        if (debouncedSearch && debouncedSearch.trim() !== "") {
          params.search = debouncedSearch.trim();
        }

        const response = await getSellerGatePasses(user.seller.id, params);
        
        const data = response.data || [];
        setGatePasses(data);

        if (response.pagination) {
          setPagination({
            currentPage: response.pagination.page,
            totalPages: response.pagination.totalPages,
            totalItems: response.pagination.total,
            itemsPerPage: response.pagination.limit,
          });
        }
      } catch (err) {
        console.error("Error fetching seller gate passes:", err);
        setError(err.response?.data?.message || "Failed to load gate passes");
      } finally {
        setLoading(false);
      }
    };

    fetchGatePasses();
  }, [user?.seller?.id, currentPage, filterStatus, debouncedSearch, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  const getGatePassStatus = (gatePass, translateFn) => {
    const validTill = dayjs(gatePass.validTillDate);
    const now = dayjs();
    if (now.isAfter(validTill)) {
      return {
        text: translateFn("historyGatePass.status.expired"),
        color: "text-white bg-red-700",
        icon: <XCircle className="w-4 h-4" />,
      };
    }
    return {
      text: translateFn("historyGatePass.status.valid"),
      color: "text-white bg-green-600",
      icon: <CheckCircle className="w-4 h-4" />,
    };
  };


  const handleDownloadGatePass = (gatePass) => {
  const status = getGatePassStatus(gatePass, translate);

    generateGatePassPdf({
      gatePass,
      translate,
      assets: { skyautologo, statusPng: status.text === translate("historyGatePass.status.expired") ? expirePng : approvedpng },
      status,
      outputMode: 'save'
    });
  };

  const handleViewGatePass = (gatePass) => {
  const status = getGatePassStatus(gatePass, translate);

    generateGatePassPdf({
      gatePass,
      translate,
      assets: { skyautologo, statusPng: status.text === translate("historyGatePass.status.expired") ? expirePng : approvedpng },
      status,
      outputMode: 'view'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">Error: {error}</div>
        <button
          onClick={() => window.location.reload()}
          className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="bg-[#FFF9F9] rounded-lg sm:rounded-xl shadow-md">
      {/* Header */}
      <div className="bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-lg sm:rounded-t-[16px] px-1 py-1">
        <div className="p-4 sm:p-5 md:p-[20px] text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold text-gray-700 bg-primary-soft border-b border-black pb-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold">
              History & Gate Pass
            </h2>
            <span className="text-xs sm:text-sm font-bold text-black">
              Total Gate Passes: {gatePasses.length}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2 sm:space-y-3 px-3 sm:px-4 md:px-5 py-3 sm:py-4">
        {/* Filter Navigation */}
        <div className="border-b border-gray-200">
          {/* Filter Tabs and Search */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 py-3 sm:py-4">
          {/* Filter Tabs */}
          <nav className="flex flex-nowrap gap-1 sm:gap-1.5 md:gap-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
            {[
              { key: 'all', label: 'All' },
              { key: 'issued', label: 'Issued' },
              { key: 'pending', label: 'Pending' },
              { key: 'active', label: 'Active' },
              { key: 'expired', label: 'Expired' },
              { key: 'used', label: 'Used' },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setFilterStatus(filter.key)}
                className={`px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-full font-medium text-[10px] sm:text-[11px] md:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === filter.key
                    ? 'bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                }`}
              >
                <span>{filter.label}</span>
              </button>
            ))}
          </nav>

          {/* Search Field */}
          <div className="flex items-center w-full lg:w-auto gap-2.5 sm:gap-3">
            <div className="relative flex-1 lg:flex-none">
              <input
                type="text"
                placeholder="Search by Lot Number, Vehicle Name, or Gate Pass Number"
                className="w-full sm:w-64 md:w-80 px-4 sm:px-5 py-2 sm:py-2.5 md:py-3 pl-10 sm:pl-12 border border-gray-300 rounded-[8px] sm:rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-xs sm:text-sm"
                value={searchTerm || ''}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-orange-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="p-2 sm:p-2.5 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                title="Clear search"
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {gatePasses.length === 0 ? (
          <div className="text-center py-8 sm:py-12 px-4">
            <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1.5 sm:mb-2">
              No gate passes found
            </h3>
            <p className="text-sm sm:text-base text-gray-500">
              {filterStatus === 'all'
                ? "You don't have any gate passes yet."
                : `No ${filterStatus} gate passes found.`}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {gatePasses.map((gatePass) => (
                <div key={gatePass.id} className="bg-white p-4 rounded-lg shadow border border-gray-100">
                  <div className="flex justify-between items-start mb-3">
                    <div className="min-w-0">
                      <h3 className="font-bold text-gray-800 truncate" title={gatePass.vehicle?.name || "N/A"}>
                        {gatePass.vehicle?.name || "N/A"}
                      </h3>
                      <p className="text-xs text-gray-500">Lot: {gatePass.vehicle?.lotNumber || "N/A"}</p>
                    </div>
                    <div className={`px-2 py-1 rounded text-[10px] font-bold whitespace-nowrap ${getGatePassStatus(gatePass, translate).color}`}>
                      {getGatePassStatus(gatePass, translate).text}
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <div className="text-xs text-gray-400">
                      GP: {gatePass.gatePassNumber || gatePass.id}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewGatePass(gatePass)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </button>
                      <button
                        onClick={() => handleDownloadGatePass(gatePass)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white rounded hover:bg-orange-600 text-xs transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {pagination.totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  setCurrentPage={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  )
}

export default HistoryAndGatepass
